import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Minus, X, Loader2, AppWindow } from "lucide-react";
import { serializable } from "../drafts/snapshot";
import { displayToast } from "../../utils/Toast";
import "./workspace.css";

export const MINIMIZED_PAGES_KEY = "minimized_pages";
const LIMIT = 12;
const WorkspaceContext = createContext(null);
export const useWorkspace = () => useContext(WorkspaceContext);

const humanize = (part) => part.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
export function pageTitle(location) {
  const state = location.state || {};
  const segments = location.pathname.split("/").filter(Boolean);
  if (segments[segments.length - 1] === "transactions" && typeof state.transactionCode === "string") {
    return `${state.transactionCode} · ${state.transactionId ? `#${state.transactionId}` : "New"}`;
  }
  return segments.map(humanize).join(" / ") || "Home";
}
const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const validWindow = (w) => w && typeof w.id === "string" && typeof w.title === "string" && typeof w.pathname === "string" && w.pathname.startsWith("/") && typeof w.search === "string";
export function readMinimizedPages() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(MINIMIZED_PAGES_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter(validWindow).slice(0, LIMIT) : [];
  } catch { return []; }
}
export const clearMinimizedPages = () => sessionStorage.removeItem(MINIMIZED_PAGES_KEY);

// System-style minimize for any routed page. The current page may register a
// handler (e.g. a draft entry saves itself first and returns the restore URL);
// otherwise the current location and its router state are captured as-is.
export function WorkspaceProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [windows, setWindows] = useState(readMinimizedPages);
  const [busy, setBusy] = useState(false);
  const handler = useRef(null);
  const current = useRef(location);
  current.current = location;
  useEffect(() => {
    try { sessionStorage.setItem(MINIMIZED_PAGES_KEY, JSON.stringify(windows)); } catch { /* storage full or unavailable: keep in memory only */ }
  }, [windows]);
  const registerMinimizeHandler = useCallback((fn) => {
    handler.current = fn;
    return () => { if (handler.current === fn) handler.current = null; };
  }, []);
  const minimize = useCallback(async () => {
    const from = current.current;
    if (busy || from.pathname === "/") return null;
    setBusy(true);
    try {
      const custom = handler.current ? await handler.current() : null;
      const entry = {
        id: newId(),
        minimizedAt: Date.now(),
        title: custom?.title || pageTitle(from),
        subtitle: custom?.subtitle || "",
        pathname: custom?.pathname || from.pathname,
        search: custom?.search ?? from.search ?? "",
        state: serializable(custom ? custom.state : from.state) ?? null,
      };
      setWindows((old) => [entry, ...old.filter((w) => w.pathname !== entry.pathname || w.search !== entry.search)].slice(0, LIMIT));
      navigate("/");
      return entry;
    } catch (e) {
      displayToast(e.message || "Unable to minimize this page.", "error");
      throw e;
    } finally { setBusy(false); }
  }, [busy, navigate]);
  const restore = useCallback((id) => {
    const target = windows.find((w) => w.id === id);
    if (!target) return;
    setWindows((old) => old.filter((w) => w.id !== id));
    navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
  }, [windows, navigate]);
  const close = useCallback((id) => setWindows((old) => old.filter((w) => w.id !== id)), []);
  const value = useMemo(() => ({ windows, busy, minimize, restore, close, registerMinimizeHandler }), [windows, busy, minimize, restore, close, registerMinimizeHandler]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function MinimizePageButton({ className = "" }) {
  const workspace = useWorkspace();
  const location = useLocation();
  if (!workspace || location.pathname === "/") return null;
  const Icon = workspace.busy ? Loader2 : Minus;
  return <button type="button" className={className} aria-label="Minimize" title="Minimize this page to the taskbar (double-click it there to reopen)" disabled={workspace.busy} onClick={() => workspace.minimize().catch(() => {})}>
    <Icon size={20} aria-hidden="true" className={workspace.busy ? "workspace-spin" : undefined} />
  </button>;
}

export function WorkspaceTaskbar({ sidebarMinimized = false }) {
  const workspace = useWorkspace();
  if (!workspace?.windows.length) return null;
  const { windows, restore, close } = workspace;
  return <nav className={`workspace-taskbar ${sidebarMinimized ? "workspace-taskbar-sidebar-minimized" : ""}`} aria-label="Minimized pages">
    <span className="workspace-taskbar-hint" aria-hidden="true"><AppWindow size={16} />Double-click to open</span>
    <ul>
      {windows.map((w) => <li key={w.id}>
        <button type="button" className="workspace-chip" title={`${w.title} — double-click to open`} aria-label={`Open ${w.title}`}
          onDoubleClick={() => restore(w.id)}
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); restore(w.id); } }}>
          <span className="workspace-chip-title">{w.title}</span>
          {w.subtitle && <span className="workspace-chip-subtitle">{w.subtitle}</span>}
        </button>
        <button type="button" className="workspace-chip-close" aria-label={`Close ${w.title}`} title="Remove from taskbar" onClick={() => close(w.id)}><X size={14} aria-hidden="true" /></button>
      </li>)}
    </ul>
  </nav>;
}
